import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";
import SeoHead from "../components/SeoHead";

export default function AmbassadorApply() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || params.get("r") || "";
  const [form, setForm] = useState(() => ({
    email: user?.email || "",
    name: user?.name || "",
    bio: "",
    audience: "",
    audienceSize: "",
    tradition: "",
    whyJoin: "",
    socials: { instagram: "", tiktok: "", x: "", youtube: "", website: "", discord: "" },
    refCode,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.bio.trim().length < 10) {
      toast.error("Tell us a bit more about yourself in the bio (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        audienceSize: form.audienceSize ? parseInt(form.audienceSize) : null,
      };
      const { data } = await api.post("/ambassador/apply", payload);
      setDone(data);
      if (data.alreadyAmbassador) {
        toast.success(data.message);
      } else if (data.alreadyApplied) {
        toast.info(data.message);
      } else {
        toast.success("Application received");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <SeoHead title="Application Received — Cauldron Ambassador Program" />
        <div className="max-w-lg w-full parchment p-10 text-center" data-testid="apply-success">
          <Sparkles className="w-10 h-10 text-secondary mx-auto" />
          <h1 className="font-serif text-3xl mt-6">Thank you.</h1>
          <p className="text-foreground/75 mt-4 leading-relaxed">{done.message}</p>
          {done.alreadyAmbassador && (
            <Link to="/ambassador/dashboard" className="mt-8 inline-flex bg-secondary text-secondary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs">
              Open your dashboard →
            </Link>
          )}
          {!done.alreadyAmbassador && (
            <Link to="/ambassadors" className="mt-8 inline-flex border border-secondary/45 text-secondary/90 px-6 py-3 rounded-sm uppercase tracking-wider text-xs hover:bg-secondary/10">
              Back to program
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="apply-page">
      <SeoHead
        title="Apply — Cauldron Ambassador Program"
        description="Apply to become a Cauldron Ambassador. Earn recurring USD + CAUL rewards by recruiting spiritual practitioners."
      />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Link to="/ambassadors" className="inline-flex items-center gap-2 text-foreground/65 hover:text-secondary text-xs uppercase tracking-wider mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to program
        </Link>
        <div className="label-mystic text-secondary mb-3">Ambassador Application</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Tell us about your circle.</h1>
        <p className="text-foreground/70 mt-4 leading-relaxed max-w-xl">
          We review every application by hand. The more detail you share about your audience and
          practice, the faster we can approve. Founding spots are limited.
        </p>

        <form onSubmit={submit} className="mt-12 space-y-6" data-testid="apply-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="apply-name" />
            </Field>
            <Field label="Email" required>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="apply-email" />
            </Field>
          </div>

          <Field label="Your tradition / practice" hint="Ifá · Reiki · Hoodoo · Tarot · Astrology · Espiritismo · Palo · etc.">
            <input type="text" value={form.tradition} onChange={(e) => setForm({ ...form, tradition: e.target.value })}
              className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="apply-tradition" />
          </Field>

          <Field label="Tell us about yourself + your practice" required>
            <textarea required rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Lineage, years of practice, what you teach, what makes your work distinct…"
              className="w-full bg-input border border-secondary/20 p-3 rounded-sm leading-relaxed" data-testid="apply-bio" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your audience" hint="Who follows you?" required>
              <input type="text" required value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                placeholder="Spiritual seekers in NYC, Reiki students globally, …"
                className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="apply-audience" />
            </Field>
            <Field label="Approx. audience size" hint="Combined social + email">
              <input type="number" min="0" value={form.audienceSize} onChange={(e) => setForm({ ...form, audienceSize: e.target.value })}
                className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="apply-audience-size" />
            </Field>
          </div>

          <Field label="Why Cauldron?">
            <textarea rows={3} value={form.whyJoin} onChange={(e) => setForm({ ...form, whyJoin: e.target.value })}
              placeholder="What about the platform resonates with what you're building?"
              className="w-full bg-input border border-secondary/20 p-3 rounded-sm leading-relaxed" data-testid="apply-why" />
          </Field>

          <div className="parchment p-5">
            <div className="label-mystic text-foreground/65 mb-3">Where can people find you?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["instagram", "Instagram", "@handle or url"],
                ["tiktok", "TikTok", "@handle or url"],
                ["x", "X / Twitter", "@handle or url"],
                ["youtube", "YouTube", "@channel or url"],
                ["website", "Website", "https://…"],
                ["discord", "Discord", "server or @user"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-foreground/55 mb-1">{label}</div>
                  <input type="text" value={form.socials[k]} placeholder={ph}
                    onChange={(e) => setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } })}
                    className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm text-sm" data-testid={`apply-social-${k}`} />
                </div>
              ))}
            </div>
          </div>

          {refCode && (
            <div className="text-xs text-foreground/60" data-testid="apply-ref-attribution">
              Referred by code <strong className="text-secondary">{refCode}</strong>
            </div>
          )}

          <button type="submit" disabled={submitting}
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/85 py-4 rounded-sm uppercase tracking-[0.2em] text-xs font-semibold transition disabled:opacity-50"
            data-testid="apply-submit-btn">
            {submitting ? "Submitting…" : "Submit Application"}
          </button>

          <p className="text-[11px] text-foreground/55 leading-relaxed text-center">
            By applying, you agree to our community guidelines. Approval is at our discretion.
            We review applications within 3–5 business days.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="label-mystic text-foreground/65 mb-2 block">
        {label} {required && <span className="text-secondary">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-foreground/55 mt-1">{hint}</p>}
    </div>
  );
}
